import React, { useContext, useEffect, useRef, useState } from 'react';
import { Alert, Anchor, Skeleton } from '@mantine/core';
import { Link } from 'react-router-dom';
import { AlertTriangle, Clock } from 'tabler-icons-react';
import { Context as RegistrationContext } from '../../../providers/RegistrationProvider';
import OrderListItem from './OrderListItem';
import AppTitle from '../../common/AppTitle';
import HeaderView from '../../common/HeaderView';
import { useMediaQueryIndex } from '../../../helpers/hooks';
import { getResponsiveStyle as rs } from '../../../helpers/styles';
import AppCard from '../../common/AppCard';
import AppFlexbox from '../../common/AppFlexbox';
import AppText from '../../common/AppText';
import PaginationList from '../../common/PaginationList';
import {
  getCustomerPaymentAlerts,
  PROCESSING_COLOR
} from '../../../helpers/customerPaymentsHelper';

const OrderHistory = () => {
  const mqIndex = useMediaQueryIndex();
  const hasFetched = useRef(false);
  const [paymentsError, setPaymentsError] = useState(null);
  const { state, fetchCartHistory, fetchCustomerPayments } = useContext(
    RegistrationContext
  );
  const isLoading = !hasFetched.current || state.regCartHistory.loading;
  const regCartHistory = state.regCartHistory.value
    .filter((f) => f.cart && f.cart.lineItems.length > 0)
    .sort(
      (a, b) => new Date(b?.cart.purchaseDate) - new Date(a?.cart.purchaseDate)
    );

  const arePaymentsLoading = state.customerPayments.loading;
  const {
    failedPaymentPlans,
    processingPaymentPlans,
    outstandingInvoices
  } = getCustomerPaymentAlerts(
    paymentsError || arePaymentsLoading ? null : state.customerPayments.value
  );

  const failedPaymentPlansByCart = new Map();
  failedPaymentPlans.forEach((p) => {
    if (p.pkRegCart != null && !failedPaymentPlansByCart.has(p.pkRegCart)) {
      failedPaymentPlansByCart.set(p.pkRegCart, p);
    }
  });

  const processingPaymentPlansByCart = new Map();
  processingPaymentPlans.forEach((p) => {
    if (
      p.pkRegCart != null &&
      !failedPaymentPlansByCart.has(p.pkRegCart) &&
      !processingPaymentPlansByCart.has(p.pkRegCart)
    ) {
      processingPaymentPlansByCart.set(p.pkRegCart, p);
    }
  });

  const outstandingInvoicesByCart = new Map();
  outstandingInvoices.forEach((i) => {
    if (i.pkRegCart != null) {
      const existing = outstandingInvoicesByCart.get(i.pkRegCart);
      if (!existing || (!existing.isPayable && i.isPayable)) {
        outstandingInvoicesByCart.set(i.pkRegCart, i);
      }
    }
  });

  const hasPaymentAlert =
    failedPaymentPlansByCart.size > 0 ||
    processingPaymentPlansByCart.size > 0 ||
    outstandingInvoices.length > 0;

  useEffect(() => {
    fetchCartHistory();
    setPaymentsError(null);
    fetchCustomerPayments(false, null, (error) => setPaymentsError(error));
    hasFetched.current = true;
  }, []);

  return (
    <HeaderView
      maxViewWidth={1100}
      viewProps={{
        style: {
          gap: rs([10, 10, 20], mqIndex),
          marginBottom: rs([10, 10, 40], mqIndex)
        }
      }}
    >
      {isLoading ? (
        <Skeleton
          height={44}
          style={{ marginTop: rs([10, 10, 0], mqIndex) }}
          width={250}
        />
      ) : (
        <AppTitle style={{ marginTop: rs([5, 5, 0], mqIndex) }}>
          Your Orders
        </AppTitle>
      )}

      {!isLoading && hasPaymentAlert && (
        <Alert
          color={failedPaymentPlansByCart.size > 0 ? 'red' : PROCESSING_COLOR}
          icon={
            failedPaymentPlansByCart.size > 0 ? (
              <AlertTriangle style={{ width: 30, height: 30 }} />
            ) : (
              <Clock style={{ width: 30, height: 30 }} />
            )
          }
          variant="outline"
        >
          <AppFlexbox
            style={{
              gap: 10,
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap'
            }}
          >
            <AppText style={{ fontSize: 16 }} weight={500}>
              {failedPaymentPlansByCart.size > 0
                ? 'A payment failed and new card details are needed.'
                : outstandingInvoices.length > 0
                ? 'You have an outstanding invoice.'
                : 'A scheduled payment is still processing.'}
            </AppText>
            <Anchor component={Link} to="/payments">
              <AppText style={{ fontSize: 16 }}>View your payments</AppText>
            </Anchor>
          </AppFlexbox>
        </Alert>
      )}

      {!isLoading && paymentsError && (
        <Alert
          color="gray"
          icon={<AlertTriangle style={{ width: 30, height: 30 }} />}
          variant="outline"
        >
          <AppFlexbox
            style={{
              gap: 10,
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap'
            }}
          >
            <AppText style={{ fontSize: 16 }} weight={500}>
              We couldn’t load your payment status right now.
            </AppText>
            <Anchor component={Link} to="/payments">
              <AppText style={{ fontSize: 16 }}>View your payments</AppText>
            </Anchor>
          </AppFlexbox>
        </Alert>
      )}

      {!isLoading && regCartHistory.length === 0 ? (
        <AppCard>
          <AppFlexbox
            style={{
              alignSelf: 'center',
              justifyContent: 'center',
              padding: 40,
              gap: 10
            }}
          >
            <AppText style={{ fontSize: 16 }}>
              You have not placed any orders.
            </AppText>
            <Anchor component={Link} to="/leagues">
              <AppText style={{ fontSize: 16 }}>
                View available registrations
              </AppText>
            </Anchor>
          </AppFlexbox>
        </AppCard>
      ) : (
        <PaginationList
          isLoading={isLoading}
          items={regCartHistory.map((c) => (
            <OrderListItem
              key={c.pkRegCart}
              failedPaymentPlan={
                failedPaymentPlansByCart.get(c.pkRegCart) ?? null
              }
              outstandingInvoice={
                outstandingInvoicesByCart.get(c.pkRegCart) ?? null
              }
              processingPaymentPlan={
                processingPaymentPlansByCart.get(c.pkRegCart) ?? null
              }
              regCart={c}
            />
          ))}
          itemsPerPage={5}
          listProps={{ style: { gap: 20 } }}
          LoadingComponent={OrderListItem}
          noBorder
        />
      )}
    </HeaderView>
  );
};

export default OrderHistory;
